import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, Subject } from 'rxjs';
import { JoyrideService } from 'ngx-joyride';
import { Router } from '@angular/router';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { ApiService } from './api.service';

@Injectable({
  providedIn: 'root'
})
export class TourService {
  constructor(
    private joyrideService: JoyrideService,
    private http: HttpClient,
    private router: Router,
    private api: ApiService,
  ) {}
  private joyrideCloseHandler: any = null;
  private isTourClosedEarly: boolean = false;

  userType = localStorage.getItem('userType');
  userId = localStorage.getItem('userId');

  private landingTourCompletedSubject = new Subject<void>();
  landingTourCompleted$ = this.landingTourCompletedSubject.asObservable();

  hasShownBackupEmailPrompt(): boolean {
    return localStorage.getItem('backupEmailPromptShown') === 'true';
  }

  markBackupEmailPromptShown(): void {
    localStorage.setItem('backupEmailPromptShown', 'true');
  }

  /** True while the landing-page onboarding tour still needs to finish. */
  isLandingTourPending(): boolean {
    if (localStorage.getItem('userType') === 'JOB_SEEKER') {
      return false;
    }
    if (localStorage.getItem('tourshowlater') === 'true') {
      return false;
    }
    return localStorage.getItem('tourCompleted_LangdingPageNavBarComponent') !== 'true';
  }

  markLandingTourComplete(source: string): void {
    console.log('[TourService] ===== markLandingTourComplete CALLED =====');
    console.log('[TourService] markLandingTourComplete from:', source);
    localStorage.setItem('tourCompleted_LangdingPageNavBarComponent', 'true');
    document.body.classList.remove('noscroll');
    console.log('[TourService] isLandingTourPending after mark:', this.isLandingTourPending());
    console.log('[TourService] Emitting landingTourCompletedSubject');
    this.landingTourCompletedSubject.next();
    console.log('[TourService] landingTourCompletedSubject emitted');
  }

  // Direct callback for when tour is closed via X button
  onTourClosed(): void {
    console.log('[TourService] ===== onTourClosed CALLED =====');
    this.markLandingTourComplete('tour-closed-x-button');
  }

  notifyLandingTourCompleted(): void {
    this.markLandingTourComplete('notifyLandingTourCompleted');
  }

  currentSteps:any;
  // Method to get user-specific tour steps
  public getTourSteps(screenName: string): Observable<any[]> {
    const userType = localStorage.getItem('userType');
    // console.log('Fetching tour steps for userType:', userType, 'and screenName:', screenName);
    return this.http.get<any[]>('assets/tour-steps.json').pipe(
      map(steps => steps.filter(step => step.userType === userType && step.screenName === screenName)),
      tap(steps => console.log())
    );
  }
  currentStep: any;

  // Method to start the tour for a specific component
  public startTourForComponent(componentId: string, screenName: string, fromStep?: string): void {
    console.log('[TourService] startTourForComponent called:', { componentId, screenName, fromStep });

    if (localStorage.getItem(`tourCompleted_${componentId}`)) {
      console.log(`[TourService] Tour for ${componentId} already completed`);
      return;
    }

    // Mark tour as started when beginning a new tour (not resuming)
    if (!fromStep) {
      this.markTourStarted(componentId);
    }

    this.getTourSteps(screenName).pipe(
      map(steps => steps.filter(step => step.componentId === componentId)),
      tap(steps => {
        if (steps.length === 0) {
          throw new Error(`No tour steps found for component: ${componentId} on screen: ${screenName}`);
        }
      }),
      switchMap(steps => {
        const initialStep = fromStep || this.getLastTourStep(componentId) || steps[0].stepId;
        const initialRoute = steps.find(step => step.stepId === initialStep)?.route;

        if (initialRoute && this.router.url !== initialRoute) {
          return this.router.navigateByUrl(initialRoute).then(success => {
            if (!success) {
              throw new Error(`Navigation failed to: ${initialRoute}`);
            }
            return steps;
          });
        } else {
          return of(steps);
        }
      })
    ).subscribe({
      next: steps => {
        const stepIds = steps.map(step => step.stepId);
        const initialStep = fromStep || this.getLastTourStep(componentId) || steps[0].stepId;
        console.log('Starting tour with steps:', stepIds);
        document.body.classList.add("noscroll");
        this.joyrideService.startTour({
          steps: stepIds,
          stepDefaultPosition: 'bottom',
          showCounter: true,
          logsEnabled: false,
          startWith: initialStep,
          waitingTime: 500
        }).subscribe({
          next: (step: any) => {
              localStorage.setItem(`lastTourStep_${componentId}`, step.name);
             this.updateTour(screenName,step.name);
             this.currentStep = step.name;
             if (step.name === 'step.userbadge' || step.name === 'start.tour') {
               window.scrollTo(0, 0);
             }
             this.observeStepTarget(step.name);

             // Handle backdrop border-radius for tour steps

          },
          complete: () => {
            console.log('[TourService] joyride complete callback fired for', componentId);
            this.stopObservingStepTarget();
            this.updateTour(screenName, this.currentStep);
            localStorage.setItem(`tourCompleted_${componentId}`, 'true');
            this.clearTourStarted(componentId); // Clear tour started flag on completion
           this.updateTourAPI();
           // For LangdingPageNavBarComponent, trigger the backup email prompt
           if (componentId === 'LangdingPageNavBarComponent') {
             console.log('[TourService] LangdingPageNavBarComponent tour complete, calling markLandingTourComplete');
             setTimeout(() => {
    this.markLandingTourComplete('joyride-complete-callback');
  }, 100);
           } else {
             document.body.classList.remove('noscroll');
           }
           },
          error: (error) => {
            console.log('[TourService] ===== ERROR CALLBACK TRIGGERED =====');
           console.log('[TourService] Error details:', error);
            this.stopObservingStepTarget();
            document.body.classList.remove("noscroll");
            // For LangdingPageNavBarComponent, trigger the backup email prompt even on close/error
            if (componentId === 'LangdingPageNavBarComponent') {
              console.log('[TourService] LangdingPageNavBarComponent tour closed/error, calling markLandingTourComplete');
              this.markLandingTourComplete('joyride-error-callback');
              // Also emit the subject to ensure the component receives the notification
              setTimeout(() => {
                console.log('[TourService] Re-emitting landingTourCompletedSubject for error case');
                this.landingTourCompletedSubject.next();
              }, 100);
            }
            console.error('Tour error:', error);
          }
        });
      },
      error: error => {
        console.error('Error starting tour:', error);
      }
    });
  }

  private activeResizeObserver: ResizeObserver | null = null;
  private activeBackdropInterval: any = null;

  /** Dynamically adjust the Joyride backdrop cutout height and position if the target
   *  element changes dimensions (e.g., async API loading, skeleton replacement, image rendering). */
  private observeStepTarget(stepName: string) {
    this.stopObservingStepTarget();

    const findAndObserve = () => {
      const targetEl = document.querySelector(`[joyrideStep="${stepName}"]`) as HTMLElement;
      if (!targetEl) return;

      const updateBackdropDimensions = () => {
        const backdropContainer = document.querySelector('.backdrop-container') as HTMLElement;
        if (!backdropContainer || !targetEl) return;

        const rect = targetEl.getBoundingClientRect();
        const middleContainer = backdropContainer.querySelector('.backdrop-middle-container') as HTMLElement;
        const targetBackdrop = backdropContainer.querySelector('.backdrop-target') as HTMLElement;
        const topBackdrop = backdropContainer.querySelector('.backdrop-top') as HTMLElement;
        const leftBackdrop = backdropContainer.querySelector('.backdrop-left') as HTMLElement;

        if (middleContainer && targetEl.offsetHeight > 0) {
          const currentHeight = parseInt(middleContainer.style.height || '0', 10);
          if (Math.abs(currentHeight - targetEl.offsetHeight) > 2) {
            middleContainer.style.height = `${targetEl.offsetHeight}px`;
            window.dispatchEvent(new Event('resize'));
          }
        }
        if (targetBackdrop && targetEl.offsetWidth > 0) {
          targetBackdrop.style.width = `${targetEl.offsetWidth}px`;
        }
        if (topBackdrop) {
          const scrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
          const targetTop = rect.top + scrollY;
          topBackdrop.style.height = `${Math.max(0, targetTop - scrollY)}px`;
        }
        if (leftBackdrop) {
          const scrollX = window.pageXOffset || document.documentElement.scrollLeft || 0;
          const targetLeft = rect.left + scrollX;
          leftBackdrop.style.width = `${Math.max(0, targetLeft - scrollX)}px`;
        }
      };

      updateBackdropDimensions();
      requestAnimationFrame(updateBackdropDimensions);

      if (typeof ResizeObserver !== 'undefined') {
        this.activeResizeObserver = new ResizeObserver(() => {
          updateBackdropDimensions();
        });
        this.activeResizeObserver.observe(targetEl);
      }

      // Check periodically for 4 seconds in case of slow async API/image responses
      let checks = 0;
      this.activeBackdropInterval = setInterval(() => {
        checks++;
        updateBackdropDimensions();
        if (checks > 20) {
          if (this.activeBackdropInterval) {
            clearInterval(this.activeBackdropInterval);
            this.activeBackdropInterval = null;
          }
        }
      }, 200);
    };

    setTimeout(findAndObserve, 60);
  }

  private stopObservingStepTarget() {
    if (this.activeResizeObserver) {
      this.activeResizeObserver.disconnect();
      this.activeResizeObserver = null;
    }
    if (this.activeBackdropInterval) {
      clearInterval(this.activeBackdropInterval);
      this.activeBackdropInterval = null;
    }
  }
   userGuidelineslocal:any[] = [];
   combinedData: any[] = [];
   originalData2 :any[] = [];
  updateTour(screen: string,id: any){
    this.userGuidelineslocal.push({ tagName:screen,tagOrder:id } );
  }

  updateTourAPI() {
    this.api.query("user/" +localStorage.getItem('userId')).subscribe((res) => {
      let response: any = res;

      // Guard the parse: stored can be "null"/"undefined"/invalid JSON — e.g. login
      // does JSON.stringify(res.data.userGuidelines) and when the server returns no
      // guidelines that yields the string "undefined". A raw JSON.parse("undefined")
      // THROWS, which would skip the POST below and silently drop the update. Parse
      // defensively and fall back to just the locally-collected tags.
      let getdatafromAPI: any[] = [];
      const stored = localStorage.getItem("userGuidelines");
      if (stored && stored !== "null" && stored !== "undefined") {
        try {
          const parsed = JSON.parse(stored);
          if (Array.isArray(parsed)) {
            getdatafromAPI = parsed;
          }
        } catch (e) {
          getdatafromAPI = [];
        }
      }
      this.combinedData = [...this.userGuidelineslocal, ...getdatafromAPI];
      response.userGuidelines = this.getLastTagObjects(this.combinedData);

      this.api.create("user/updateUserGuidelines", response).subscribe((respo) => {
        if (respo.code == "00000") {
          localStorage.setItem('userGuidelines', JSON.stringify(response.userGuidelines));
          console.log('Updated tour data in localStorage:', response.userGuidelines);
        }
      });
    });
  }

  getLastTagObjects(data: any[]): any[] {
    const tagMap = new Map<string, any>();

    data.forEach(item => {
      tagMap.set(item.tagName, item);
    });

    return Array.from(tagMap.values());
  }

  // Method to resume the tour for a specific component
  public resumeTourForComponent(componentId: string, screenName: string): void {
    const lastStep = localStorage.getItem(`lastTourStep_${componentId}`);
    if (lastStep) {
      this.startTourForComponent(componentId, screenName, lastStep);
    } else {
      console.log(`No step to resume from for component ${componentId} on screen ${screenName}, starting tour from the beginning.`);
      this.startTourForComponent(componentId, screenName);
    }
  }

  isTourInProgress(componentId: string): boolean {
    const isCompleted = localStorage.getItem(`tourCompleted_${componentId}`) === 'true';
    const lastStep = localStorage.getItem(`lastTourStep_${componentId}`);
    const tourStarted = localStorage.getItem(`tourStarted_${componentId}`) === 'true';
    const hasLastStep = lastStep !== null;
    // Tour is in progress if not completed and either has a last step or was started
    const inProgress = !isCompleted && (hasLastStep || tourStarted);
    console.log(`[TourService] isTourInProgress for ${componentId}:`, {
      isCompleted,
      lastStep,
      tourStarted,
      hasLastStep,
      inProgress
    });
    return inProgress;
  }

  

  // Method to get the last tour step for a specific component
  private getLastTourStep(componentId: string): string | null {
    return localStorage.getItem(`lastTourStep_${componentId}`);
  }

  // Method to mark tour as started
  public markTourStarted(componentId: string): void {
    localStorage.setItem(`tourStarted_${componentId}`, 'true');
    console.log(`[TourService] Marked tour as started for ${componentId}`);
  }

  // Method to clear tour started flag
  public clearTourStarted(componentId: string): void {
    localStorage.removeItem(`tourStarted_${componentId}`);
    console.log(`[TourService] Cleared tour started flag for ${componentId}`);
  }

  // Method to navigate to the next step's route
  private navigateToStep(stepId: string, steps: any[]): void {
    const step = steps.find(s => s.stepId === stepId);
    if (step && step.route) {
      if (this.router.url !== step.route) {
        this.router.navigateByUrl(step.route).then(success => {
          if (!success) {
            console.error(`Navigation failed to: ${step.route}`);
          }
        });
      } else {
        console.log('Already on the target route:', step.route);
      }
    }
  }
}
